import { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Stethoscope } from "lucide-react";
import { LANDING_PAGES } from "../../data/landingPages";

// Every landing page carries a `canonicalExpertise` slug that maps to
// /expertise/<slug>. Some canonicalExpertise slugs (e.g. "premium-cataract-surgery")
// map to more than ONE landing page — we keep them all so a single blog can
// surface both the "premium" and "complex" cataract variants.
const _byCanonical = LANDING_PAGES.reduce((acc, p) => {
  if (!p.canonicalExpertise) return acc;
  if (!acc.has(p.canonicalExpertise)) acc.set(p.canonicalExpertise, []);
  acc.get(p.canonicalExpertise).push(p);
  return acc;
}, new Map());

// Category / keyword fallback so blogs without `related_expertise` still
// surface something useful. Keys are lowercase substrings we look for in
// the post's `category` or `tags`; values are either a `canonicalExpertise`
// slug (e.g. "medical-retina") or a direct landing-page slug (e.g.
// "pediatric-ophthalmologist-in-varanasi") — the picker below handles both.
const CATEGORY_HINTS = [
  ["cataract", "premium-cataract-surgery"],
  ["retina", "medical-retina"],
  ["retina", "diabetic-retinopathy"],
  ["diabet", "diabetic-retinopathy"],
  ["diabet", "medical-retina"],
  ["squint", "squint-surgery-in-varanasi"],
  ["strabismus", "squint-surgery-in-varanasi"],
  ["cornea", "cornea-and-refractive"],
  ["glaucoma", "glaucoma-treatment-in-varanasi"],
  ["paediatric", "pediatric-ophthalmologist-in-varanasi"],
  ["pediatric", "pediatric-ophthalmologist-in-varanasi"],
  ["paediatric", "rop-screening-in-varanasi"],
  ["dry eye", "comprehensive-eye-examination-in-varanasi"],
  ["amd", "medical-retina"],
  ["macular", "medical-retina"],
  ["injection", "medical-retina"],
];

// Quick lookup by direct landing-page slug so CATEGORY_HINTS can point
// at either a canonicalExpertise or a landing slug interchangeably.
const _bySlug = new Map(LANDING_PAGES.map((p) => [p.slug, p]));

/**
 * PopularServices — display-only "Popular services in Varanasi" strip for
 * blog posts. NO database writes; NO changes to blog documents. Purely
 * derives the top 3 relevant service landing pages from the blog's existing
 * `related_expertise`, `category` and `tags` fields.
 *
 * Props:
 *   post     object  The blog post document (needs .category, .tags,
 *                    .related_expertise).
 *   limit    number  Max cards (default 3).
 */
const PopularServices = ({ post, limit = 3 }) => {
  const items = useMemo(() => {
    if (!post) return [];
    const picked = new Set();
    const out = [];

    // 1. Explicit related_expertise from the post — highest signal.
    for (const slug of post.related_expertise || []) {
      const pages = _byCanonical.get(slug) || [];
      for (const p of pages) {
        if (!picked.has(p.slug)) {
          picked.add(p.slug);
          out.push(p);
          if (out.length >= limit) return out;
        }
      }
    }

    // 2. Category / tag hints. `expOrSlug` may be a canonicalExpertise slug
    // (mapped to 1+ landing pages) or a direct landing-page slug.
    const haystack = [
      (post.category || "").toLowerCase(),
      ...(post.tags || []).map((t) => (t || "").toLowerCase()),
      (post.title || "").toLowerCase(),
    ].join(" ");
    for (const [needle, expOrSlug] of CATEGORY_HINTS) {
      if (out.length >= limit) break;
      if (!haystack.includes(needle)) continue;
      const candidates = _byCanonical.get(expOrSlug) || (
        _bySlug.has(expOrSlug) ? [_bySlug.get(expOrSlug)] : []
      );
      for (const p of candidates) {
        if (!picked.has(p.slug)) {
          picked.add(p.slug);
          out.push(p);
          if (out.length >= limit) break;
        }
      }
    }

    // 3. Final fallback — the flagship cataract landing page so the strip
    // never renders empty on a lightly-tagged post.
    if (out.length === 0) {
      const flagship = LANDING_PAGES.find(
        (p) => p.slug === "premium-cataract-surgery-in-varanasi"
      );
      if (flagship) out.push(flagship);
    }

    return out.slice(0, limit);
  }, [post, limit]);

  if (!items.length) return null;

  return (
    <section
      className="py-14 border-t border-border bg-[hsl(var(--secondary))]/40"
      data-testid="popular-services"
    >
      <div className="container-page">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent))] mb-3">
          Popular in Varanasi
        </div>
        <h2 className="font-serif text-3xl">Services related to this article</h2>
        <div className="h-[2px] w-12 bg-[hsl(var(--accent))] mt-3 mb-6" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((s) => (
            <Link
              key={s.slug}
              to={`/${s.slug}`}
              data-testid={`popular-service-${s.slug}`}
              className="group border border-border rounded-xl p-5 hover:border-[hsl(var(--primary))] transition-colors flex flex-col"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-1.5">
                  <Stethoscope size={11} /> {s.keyword}
                </div>
                <ArrowUpRight
                  size={16}
                  strokeWidth={1.5}
                  className="text-[hsl(var(--accent))] shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                />
              </div>
              <div className="font-serif text-lg mt-3 leading-snug group-hover:text-[hsl(var(--primary))]">
                {s.h1}
              </div>
              {s.tagline && (
                <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{s.tagline}</p>
              )}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PopularServices;
